import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import ActionBar from '../components/ActionBar';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { CountrySelector, PhoneInput } from '../components/CountryInput';
import { AlertCircle, ChevronRight, ChevronDown, CalendarDays, Users, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export default function BookingPage() {
  const { t, language, formatPrice, currency, BACKEND_URL, getImageUrl } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const roomId = searchParams.get('room_id');
  const checkIn = searchParams.get('check_in');
  const checkOut = searchParams.get('check_out');
  const guests = searchParams.get('guests') || '2';
  const childAges = (searchParams.get('children') || '').split(',').filter(s => s !== '').map(Number).filter(n => !isNaN(n) && n >= 0);

  const [room, setRoom] = useState(null);
  const [priceInfo, setPriceInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '',
    phone: '', dial_code: '+90',
    nationality_code: 'TR', identity_number: '', address: '',
    special_requests: '',
  });

  const nights = checkIn && checkOut
    ? Math.round((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)) : 0;

  // Set page title
  useEffect(() => {
    document.title = language === 'tr'
      ? 'Rezervasyon Yap | Kastamonu Amorium Hotel | 4 Mevsim Doğa Tatili'
      : 'Make Reservation | Kastamonu Amorium Hotel | 4 Seasons Nature Holiday';
  }, [language]);

  useEffect(() => {
    if (!roomId || !checkIn || !checkOut) { navigate('/rooms'); return; }
    Promise.all([
      axios.get(`${BACKEND_URL}/api/rooms/${roomId}`),
      axios.post(`${BACKEND_URL}/api/pricing/calculate`, { room_id: roomId, check_in: checkIn, check_out: checkOut, currency: 'TRY', guests: parseInt(guests), children: childAges })
    ]).then(([roomRes, priceRes]) => {
      setRoom(roomRes.data);
      setPriceInfo(priceRes.data);
      setLoading(false);
    }).catch(() => { setLoading(false); navigate('/rooms'); });
  }, [roomId, checkIn, checkOut, BACKEND_URL, navigate]);

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = t('required_field');
    if (!form.last_name.trim()) e.last_name = t('required_field');
    if (!form.email.trim()) e.email = t('required_field');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t('invalid_email');
    if (!form.phone.trim()) e.phone = t('required_field');
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (!policyAccepted) {
      toast.error(language === 'tr'
        ? 'Devam etmek için iptal ve iade koşullarını onaylamanız gerekmektedir.'
        : 'You must accept the cancellation and refund policy to continue.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/reservations`, {
        room_id: roomId, check_in: checkIn, check_out: checkOut,
        guests: parseInt(guests), children: childAges, currency, language,
        ...form,
        phone: `${form.dial_code} ${form.phone}`,
        nationality: form.nationality_code,
      });
      navigate(`/payment?reservation_id=${res.data.id}&booking_ref=${res.data.booking_ref}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('booking_error'));
      setSubmitting(false);
    }
  };

  const set = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: '' }));
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Styles
  const S = {
    page: { minHeight: '100vh', background: '#F6F1E7', fontFamily: 'Manrope, sans-serif' },
    container: { maxWidth: '1100px', margin: '0 auto', padding: '2rem 1.5rem 4rem' },
    h1: { fontFamily: 'Cormorant Garamond, serif', fontSize: '30px', fontWeight: 600, color: '#1F3A2E', marginBottom: '24px' },
    grid: { display: 'flex', gap: '32px', alignItems: 'flex-start', flexWrap: 'wrap' },
    formCard: { flex: '1 1 420px', minWidth: 0, background: 'white', borderRadius: '16px', border: '1px solid #EFE7DA' },
    cardHeader: { padding: '18px 24px', borderBottom: '1px solid #EFE7DA', fontWeight: 700, fontSize: '15px', color: '#1F3A2E' },
    cardBody: { padding: '20px 24px' },
    twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: '14px' },
    oneCol: { marginTop: '14px' },
    label: { display: 'block', fontSize: '12px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px' },
    input: (hasError) => ({
      width: '100%', padding: '10px 12px', fontSize: '14px',
      border: `1.5px solid ${hasError ? '#dc2626' : '#D8D1C6'}`,
      borderRadius: '10px', outline: 'none', background: 'white',
      fontFamily: 'Manrope, sans-serif', boxSizing: 'border-box',
      transition: 'border-color 0.15s',
    }),
    textarea: (hasError) => ({
      width: '100%', padding: '10px 12px', fontSize: '14px',
      border: `1.5px solid ${hasError ? '#dc2626' : '#D8D1C6'}`,
      borderRadius: '10px', outline: 'none', background: 'white',
      fontFamily: 'Manrope, sans-serif', boxSizing: 'border-box',
      minHeight: '80px', resize: 'vertical',
    }),
    errorMsg: { fontSize: '12px', color: '#dc2626', marginTop: '3px' },
    submitBtn: {
      width: '100%', background: '#1F3A2E', color: 'white',
      padding: '14px', borderRadius: '12px', border: 'none', cursor: 'pointer',
      fontSize: '15px', fontWeight: 700, marginTop: '20px', fontFamily: 'Manrope, sans-serif',
      transition: 'background 0.2s',
    },
    summaryCard: { width: '320px', flexShrink: 0, position: 'sticky', top: '80px', background: 'white', borderRadius: '16px', border: '1px solid #EFE7DA', overflow: 'hidden' },
    summaryImg: { width: '100%', height: '160px', objectFit: 'cover', display: 'block' },
    summaryBody: { padding: '16px 20px' },
    row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', marginBottom: '8px' },
    rowLabel: { color: '#6E7F5B' },
    rowValue: { fontWeight: 600, color: '#1F3A2E' },
    divider: { borderTop: '1px solid #EFE7DA', margin: '10px 0' },
    totalRow: { display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '16px', color: '#1F3A2E' },
    infoBox: { background: '#F6F1E7', borderRadius: '10px', padding: '10px 12px', marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'flex-start' },
  };

  if (loading) return (
    <div style={S.page}>
      <ActionBar />
      <Navbar />
      <div style={S.container}>
        <div style={{ background: 'white', borderRadius: '16px', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6E7F5B' }}>
          {language === 'tr' ? 'Yükleniyor...' : 'Loading...'}
        </div>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <ActionBar />
      <Navbar />
      <div style={S.container}>
        {/* Breadcrumb / Stepper */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#6E7F5B', marginBottom: '20px', flexWrap: 'wrap' }}>
          <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: '3px', background: 'none', border: 'none', cursor: 'pointer', color: '#6E7F5B', fontSize: '13px', fontFamily: 'Manrope, sans-serif' }}>
            ‹ {t('rooms')}
          </button>
          <span style={{ color: '#D8D1C6' }}>/</span>
          <span style={{ color: '#1F3A2E', fontWeight: 700 }}>{language === 'tr' ? 'Kişisel Bilgiler' : 'Guest Details'}</span>
          <span style={{ color: '#D8D1C6' }}>/</span>
          <span style={{ color: '#B0A99A' }}>{t('payment_title')}</span>
        </div>

        <h1 style={S.h1}>{t('make_reservation')}</h1>

        <form onSubmit={handleSubmit}>
          <div style={S.grid}>
            {/* LEFT: Form */}
            <div style={{ flex: '1 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ ...S.formCard, flex: '0 0 auto' }}>
                <div style={S.cardHeader}>
                  {language === 'tr' ? '👤 Kişisel Bilgiler' : '👤 Personal Information'}
                </div>
                <div style={S.cardBody}>
                  <div style={S.twoCol}>
                    <div>
                      <label style={S.label}>{t('first_name')} *</label>
                      <input
                        data-testid="booking-form-first-name-input"
                        value={form.first_name}
                        onChange={e => set('first_name', e.target.value)}
                        placeholder={language === 'tr' ? 'Ahmet' : 'John'}
                        style={S.input(errors.first_name)}
                        onFocus={e => e.target.style.borderColor = '#1F3A2E'}
                        onBlur={e => e.target.style.borderColor = errors.first_name ? '#dc2626' : '#D8D1C6'}
                      />
                      {errors.first_name && <p data-testid="booking-form-error-message" style={S.errorMsg}>{errors.first_name}</p>}
                    </div>
                    <div>
                      <label style={S.label}>{t('last_name')} *</label>
                      <input
                        data-testid="booking-form-last-name-input"
                        value={form.last_name}
                        onChange={e => set('last_name', e.target.value)}
                        placeholder={language === 'tr' ? 'Yılmaz' : 'Doe'}
                        style={S.input(errors.last_name)}
                        onFocus={e => e.target.style.borderColor = '#1F3A2E'}
                        onBlur={e => e.target.style.borderColor = errors.last_name ? '#dc2626' : '#D8D1C6'}
                      />
                      {errors.last_name && <p style={S.errorMsg}>{errors.last_name}</p>}
                    </div>
                    <div>
                      <label style={S.label}>{t('email')} *</label>
                      <input
                        data-testid="booking-form-email-input"
                        type="email"
                        value={form.email}
                        onChange={e => set('email', e.target.value)}
                        placeholder="ornek@email.com"
                        style={S.input(errors.email)}
                        onFocus={e => e.target.style.borderColor = '#1F3A2E'}
                        onBlur={e => e.target.style.borderColor = errors.email ? '#dc2626' : '#D8D1C6'}
                      />
                      {errors.email && <p style={S.errorMsg}>{errors.email}</p>}
                    </div>
                    <div>
                      <label style={S.label}>{t('phone')} *</label>
                      <PhoneInput
                        dialCode={form.dial_code}
                        phone={form.phone}
                        onDialCodeChange={v => set('dial_code', v)}
                        onPhoneChange={v => set('phone', v)}
                        language={language}
                      />
                      {errors.phone && <p style={S.errorMsg}>{errors.phone}</p>}
                    </div>
                    <div>
                      <label style={S.label}>{t('nationality')}</label>
                      <CountrySelector
                        value={form.nationality_code}
                        onChange={v => set('nationality_code', v)}
                        language={language}
                      />
                    </div>
                    <div>
                      <label style={S.label}>{language === 'tr' ? 'TC / Pasaport No' : 'ID / Passport No'}</label>
                      <input value={form.identity_number} onChange={e => set('identity_number', e.target.value)} placeholder={language === 'tr' ? '12345678901' : 'A1234567'} style={S.input(false)} onFocus={e => e.target.style.borderColor = '#1F3A2E'} onBlur={e => e.target.style.borderColor = '#D8D1C6'} />
                    </div>
                  </div>
                  <div style={S.oneCol}>
                    <label style={S.label}>{language === 'tr' ? 'Adres' : 'Address'}</label>
                    <input value={form.address} onChange={e => set('address', e.target.value)} placeholder={language === 'tr' ? 'Adresiniz' : 'Your address'} style={S.input(false)} onFocus={e => e.target.style.borderColor = '#1F3A2E'} onBlur={e => e.target.style.borderColor = '#D8D1C6'} />
                  </div>
                  <div style={{ ...S.oneCol, marginBottom: '4px' }}>
                    <label style={S.label}>{t('special_requests')}</label>
                    <textarea
                      data-testid="booking-form-special-requests-textarea"
                      value={form.special_requests}
                      onChange={e => set('special_requests', e.target.value)}
                      placeholder={t('special_requests_placeholder')}
                      rows={3}
                      style={{
                        ...S.textarea(false),
                        minHeight: '90px', display: 'block', width: '100%',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Cancellation & Refund Policy */}
              <div style={{ ...S.formCard, flex: '0 0 auto' }} data-testid="cancellation-policy-card">
                <button
                  type="button"
                  onClick={() => setPolicyOpen(o => !o)}
                  data-testid="cancellation-policy-toggle"
                  style={{
                    ...S.cardHeader, display: 'flex', alignItems: 'center', gap: '8px',
                    width: '100%', textAlign: 'left', background: 'transparent',
                    border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  <FileText size={15} style={{ color: '#B08D57' }} />
                  <span style={{ flex: 1 }}>{language === 'tr' ? 'İptal ve İade Koşulları' : 'Cancellation & Refund Policy'}</span>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#6E7F5B', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    {policyOpen ? (language === 'tr' ? 'Gizle' : 'Hide') : (language === 'tr' ? 'Koşulları Oku' : 'Read Terms')}
                    <ChevronDown size={15} style={{ transform: policyOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                  </span>
                </button>
                <div style={S.cardBody}>
                  {policyOpen && (
                    <div style={{
                      background: '#F6F1E7', borderRadius: '10px', padding: '14px 16px',
                      maxHeight: '200px', overflowY: 'auto', marginBottom: '14px',
                      fontSize: '13px', color: '#3E5A3F', lineHeight: 1.7, whiteSpace: 'pre-line',
                    }} data-testid="cancellation-policy-text">
                      {(language === 'tr' ? room?.cancellation_policy_tr : room?.cancellation_policy_en)
                        || (language === 'tr'
                          ? 'İptal ve iade koşulları için lütfen otel ile iletişime geçin.'
                          : 'Please contact the hotel for cancellation and refund terms.')}
                    </div>
                  )}
                  <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer',
                    padding: '12px 14px', borderRadius: '10px',
                    border: `1.5px solid ${policyAccepted ? '#1F3A2E' : '#D8D1C6'}`,
                    background: policyAccepted ? 'rgba(31,58,46,0.05)' : 'white',
                    transition: 'border-color 0.15s, background 0.15s',
                  }}>
                    <input
                      type="checkbox"
                      checked={policyAccepted}
                      onChange={e => setPolicyAccepted(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: '#1F3A2E', flexShrink: 0, marginTop: '1px', cursor: 'pointer' }}
                      data-testid="cancellation-policy-checkbox"
                    />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#1F3A2E', lineHeight: 1.5 }}>
                      {language === 'tr'
                        ? 'İptal ve iade koşullarını okudum, kabul ediyorum. *'
                        : 'I have read and accept the cancellation and refund policy. *'}
                    </span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting || !policyAccepted}
                data-testid="booking-form-submit-button"
                style={{ ...S.submitBtn, opacity: (submitting || !policyAccepted) ? 0.55 : 1, cursor: (submitting || !policyAccepted) ? 'not-allowed' : 'pointer' }}
                onMouseEnter={e => !submitting && policyAccepted && (e.currentTarget.style.background = '#3E5A3F')}
                onMouseLeave={e => e.currentTarget.style.background = '#1F3A2E'}
              >
                {submitting
                  ? (language === 'tr' ? 'İşleniyor...' : 'Processing...')
                  : <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      {t('continue_to_payment')} <ChevronRight size={16} />
                    </span>
                }
              </button>
            </div>

            {/* RIGHT: Summary */}
            <div style={S.summaryCard}>
              {room?.images?.[0] && (
                <img src={getImageUrl(room.images[0])} alt={room?.name} style={S.summaryImg} />
              )}
              <div style={S.summaryBody}>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '18px', fontWeight: 600, color: '#1F3A2E', marginBottom: '12px' }}>
                  {room?.name}
                </p>

                <div style={{ ...S.row }}>
                  <span style={{ ...S.rowLabel, display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <CalendarDays size={13} /> {t('check_in_date')}
                  </span>
                  <span style={S.rowValue}>{formatDate(checkIn)}</span>
                </div>
                <div style={S.row}>
                  <span style={{ ...S.rowLabel, display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <CalendarDays size={13} /> {t('check_out_date')}
                  </span>
                  <span style={S.rowValue}>{formatDate(checkOut)}</span>
                </div>
                <div style={S.row}>
                  <span style={{ ...S.rowLabel, display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Users size={13} /> {t('guests')}
                  </span>
                  <span style={S.rowValue}>
                    {guests} {language === 'tr' ? 'Yetişkin' : (parseInt(guests) > 1 ? 'Adults' : 'Adult')}
                    {childAges.length > 0 && `, ${childAges.length} ${language === 'tr' ? 'Çocuk' : (childAges.length > 1 ? 'Children' : 'Child')}`}
                  </span>
                </div>
                {childAges.length > 0 && (
                  <div style={S.row}>
                    <span style={S.rowLabel}>{language === 'tr' ? 'Çocuk Yaşları' : 'Child Ages'}</span>
                    <span style={S.rowValue}>{childAges.join(', ')}</span>
                  </div>
                )}
                <div style={S.row}>
                  <span style={S.rowLabel}>{t('total_nights')}</span>
                  <span style={S.rowValue}>{nights}</span>
                </div>

                {/* Price breakdown */}
                {priceInfo?.daily_prices && (
                  <>
                    <div style={S.divider} />
                    <div style={{ maxHeight: '120px', overflowY: 'auto', marginBottom: '4px' }}>
                      {priceInfo.daily_prices.map((day, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px', color: '#6E7F5B' }}>
                          <span>{new Date(day.date).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                          <span>{formatPrice(day.price_try)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <div style={S.divider} />
                <div style={S.row}>
                  <span style={S.rowLabel}>
                    {language === 'tr' ? 'Oda Ücreti (Vergiler Hariç)' : 'Room Rate (Excl. Taxes)'}
                  </span>
                  <span style={S.rowValue}>{priceInfo ? formatPrice(priceInfo.room_subtotal_try ?? priceInfo.subtotal_try) : '-'}</span>
                </div>
                {priceInfo?.discount_total_try > 0 && (
                  <div style={S.row} data-testid="discount-row">
                    <span style={{ ...S.rowLabel, color: '#16a34a' }}>
                      🔥 {language === 'tr' ? 'İndirim' : 'Discount'}{priceInfo.discount_labels?.length ? ` — ${priceInfo.discount_labels.join(', ')}` : ''}
                    </span>
                    <span style={{ ...S.rowValue, color: '#16a34a' }}>-{formatPrice(priceInfo.discount_total_try)}</span>
                  </div>
                )}
                {priceInfo?.child_total_try > 0 && (
                  <div style={S.row} data-testid="child-fee-row">
                    <span style={S.rowLabel}>{language === 'tr' ? 'Çocuk Ücreti' : 'Child Fee'}</span>
                    <span style={S.rowValue}>{formatPrice(priceInfo.child_total_try)}</span>
                  </div>
                )}
                <div style={S.row}>
                  <span style={S.rowLabel}>KDV ({priceInfo?.vat_rate || 10}%)</span>
                  <span style={S.rowValue}>{priceInfo ? formatPrice(priceInfo.vat_amount_try) : '-'}</span>
                </div>
                <div style={S.row}>
                  <span style={S.rowLabel}>{language === 'tr' ? 'Konaklama Vergisi' : 'Accommodation Tax'} ({priceInfo?.accommodation_tax_rate ?? 1}%)</span>
                  <span style={S.rowValue}>{priceInfo ? formatPrice(priceInfo.accommodation_tax_amount_try || 0) : '-'}</span>
                </div>
                <div style={S.divider} />
                <div style={S.totalRow} data-testid="price-breakdown-total">
                  <span>{language === 'tr' ? 'Toplam (Vergiler Dahil)' : 'Total (Incl. Taxes)'}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{priceInfo ? formatPrice(priceInfo.total_try) : '-'}</span>
                </div>

                <div style={S.infoBox}>
                  <AlertCircle size={14} style={{ color: '#B08D57', flexShrink: 0, marginTop: '1px' }} />
                  <p style={{ fontSize: '12px', color: '#6E7F5B', lineHeight: 1.5 }}>
                    {language === 'tr' ? 'Ödeme bir sonraki adımda tamamlanacak.' : 'Payment will be completed in the next step.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
}
